import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaRobot, FaChartLine, FaLightbulb, FaUsers } from 'react-icons/fa';
import Section from '../../components/common/Section';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';

const PageContainer = styled(motion.div)`
  padding-top: 100px;
`;

const HeroSection = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.space['3xl']} 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url('/assets/images/pattern.svg');
    opacity: 0.03;
    pointer-events: none;
  }
`;

const HeroTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes['5xl']};
  margin-bottom: ${({ theme }) => theme.space.lg};
  position: relative;
  display: inline-block;
  
  span {
    color: ${({ theme }) => theme.colors.accent};
    font-family: ${({ theme }) => theme.fonts.accent};
    font-style: italic;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes['4xl']};
  }
`;

const HeroDescription = styled.p`
  max-width: 800px;
  margin: 0 auto ${({ theme }) => theme.space.xl};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.grey[700]};
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.space.xl};
  margin-top: ${({ theme }) => theme.space['2xl']};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const ServiceTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  margin-bottom: ${({ theme }) => theme.space.md};
  color: ${({ theme }) => theme.colors.primary};
`;

const ServiceDescription = styled.p`
  margin-bottom: ${({ theme }) => theme.space.lg};
  line-height: 1.7;
`;

const IconWrapper = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.gradient.tertiary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.space.lg};
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  color: ${({ theme }) => theme.colors.white};
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
`;

const BenefitsSection = styled.div`
  margin-top: ${({ theme }) => theme.space['3xl']};
  background: ${({ theme }) => theme.colors.light};
  padding: ${({ theme }) => theme.space['2xl']} 0;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
`;

const BenefitsTitle = styled.h2`
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes['4xl']};
  margin-bottom: ${({ theme }) => theme.space['2xl']};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const BenefitsList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.space.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const BenefitItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: ${({ theme }) => theme.space.lg};
`;

const BenefitIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.gradient.accent};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.space.md};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  color: ${({ theme }) => theme.colors.dark};
`;

const BenefitTitle = styled.h4`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  margin-bottom: ${({ theme }) => theme.space.sm};
  color: ${({ theme }) => theme.colors.primary};
`;

const BenefitDescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.grey[700]};
`;

const CTASection = styled.div`
  text-align: center;
  margin-top: ${({ theme }) => theme.space['3xl']};
  padding: ${({ theme }) => theme.space['2xl']} ${({ theme }) => theme.space.xl};
  background: ${({ theme }) => theme.colors.gradient.tertiary};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  color: ${({ theme }) => theme.colors.white};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url('/assets/images/pattern.svg');
    opacity: 0.05;
    pointer-events: none;
  }
`;

const CTATitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes['3xl']};
  margin-bottom: ${({ theme }) => theme.space.lg};
`;

const CTADescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  max-width: 800px;
  margin: 0 auto ${({ theme }) => theme.space.xl};
  line-height: 1.7;
`;

const AIAnalytics: React.FC = () => {
  return (
    <PageContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Section>
        <HeroSection>
          <HeroTitle>AI-Powered <span>Analytics</span></HeroTitle>
          <HeroDescription>
            Harness the power of artificial intelligence to transform your data into actionable insights that drive business growth and competitive advantage.
          </HeroDescription>
          <Button variant="accent" size="large">Discover AI Solutions</Button>
        </HeroSection>
        
        <ServicesGrid>
          <Card elevation="medium" hoverEffect>
            <IconWrapper>
              <FaRobot />
            </IconWrapper>
            <ServiceTitle>Predictive Analytics</ServiceTitle>
            <ServiceDescription>
              Our AI-powered predictive analytics solutions help you forecast trends, customer behavior, and market changes before they happen. Make proactive decisions based on data-driven predictions and stay ahead of the competition.
            </ServiceDescription>
            <Button variant="text">Learn More</Button>
          </Card>
          
          <Card elevation="medium" hoverEffect>
            <IconWrapper>
              <FaChartLine />
            </IconWrapper>
            <ServiceTitle>Customer Insights</ServiceTitle>
            <ServiceDescription>
              Understand your customers like never before with our AI-driven customer analytics. Identify patterns, preferences, and pain points to create personalized experiences that increase satisfaction and loyalty.
            </ServiceDescription>
            <Button variant="text">Learn More</Button>
          </Card>
          
          <Card elevation="medium" hoverEffect>
            <IconWrapper>
              <FaLightbulb />
            </IconWrapper>
            <ServiceTitle>Intelligent Reporting</ServiceTitle>
            <ServiceDescription>
              Transform complex data into clear, actionable reports with our intelligent reporting solutions. Our AI algorithms identify the most important insights and present them in an easy-to-understand format.
            </ServiceDescription>
            <Button variant="text">Learn More</Button>
          </Card>
          
          <Card elevation="medium" hoverEffect>
            <IconWrapper>
              <FaUsers />
            </IconWrapper>
            <ServiceTitle>Market Intelligence</ServiceTitle>
            <ServiceDescription>
              Stay informed about market trends, competitor activities, and industry developments with our AI-powered market intelligence. Make strategic decisions based on comprehensive market analysis.
            </ServiceDescription>
            <Button variant="text">Learn More</Button>
          </Card>
        </ServicesGrid>
        
        <BenefitsSection>
          <BenefitsTitle>Benefits of AI-Powered Analytics</BenefitsTitle>
          <BenefitsList>
            <BenefitItem>
              <BenefitIcon>
                <FaChartLine />
              </BenefitIcon>
              <BenefitTitle>Increased Accuracy</BenefitTitle>
              <BenefitDescription>
                AI algorithms analyze data with greater precision than traditional methods, reducing errors and improving the reliability of insights.
              </BenefitDescription>
            </BenefitItem>
            
            <BenefitItem>
              <BenefitIcon>
                <FaLightbulb />
              </BenefitIcon>
              <BenefitTitle>Faster Decision Making</BenefitTitle>
              <BenefitDescription>
                Get insights in real-time, allowing you to make quick, informed decisions that keep you ahead of market changes.
              </BenefitDescription>
            </BenefitItem>
            
            <BenefitItem>
              <BenefitIcon>
                <FaUsers />
              </BenefitIcon>
              <BenefitTitle>Personalized Experiences</BenefitTitle>
              <BenefitDescription>
                Use AI-driven insights to create tailored experiences for your customers, increasing engagement and satisfaction.
              </BenefitDescription>
            </BenefitItem>
            
            <BenefitItem>
              <BenefitIcon>
                <FaRobot />
              </BenefitIcon>
              <BenefitTitle>Automated Analysis</BenefitTitle>
              <BenefitDescription>
                Save time and resources with automated data analysis that continuously monitors performance and identifies opportunities.
              </BenefitDescription>
            </BenefitItem>
            
            <BenefitItem>
              <BenefitIcon>
                <FaChartLine />
              </BenefitIcon>
              <BenefitTitle>Competitive Advantage</BenefitTitle>
              <BenefitDescription>
                Gain a significant edge over competitors by leveraging AI to uncover insights they might miss with traditional analytics.
              </BenefitDescription>
            </BenefitItem>
            
            <BenefitItem>
              <BenefitIcon>
                <FaLightbulb />
              </BenefitIcon>
              <BenefitTitle>Scalable Solutions</BenefitTitle>
              <BenefitDescription>
                Our AI analytics solutions grow with your business, handling increasing data volumes without compromising performance.
              </BenefitDescription>
            </BenefitItem>
          </BenefitsList>
        </BenefitsSection>
        
        <CTASection>
          <CTATitle>Ready to Unlock the Power of Your Data?</CTATitle>
          <CTADescription>
            Let our team of AI and analytics experts help you transform your data into a strategic asset. Schedule a consultation today to learn how our AI-powered analytics solutions can drive growth for your business.
          </CTADescription>
          <Button variant="accent" size="large">Get Started</Button>
        </CTASection>
      </Section>
    </PageContainer>
  );
};

export default AIAnalytics;
